import React from 'react';
import { Helmet } from 'react-helmet-async';
import './Privacy.css';

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Safarnamah by Saket and Pallavi</title>
        <meta name="description" content="Privacy Policy for Safarnamah - Learn how we collect, use, and protect your personal information." />
      </Helmet>

      <div className="privacy-page">
        <div className="privacy-hero">
          <div className="container">
            <h1>Privacy Policy</h1>
            <p className="last-updated">Last Updated: November 14, 2025</p>
          </div>
        </div>

        <div className="privacy-content">
          <div className="container">
            <div className="privacy-section">
              <h2>Introduction</h2>
              <p>
                Welcome to Safarnamah by Saket and Pallavi ("we," "us," or "our"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </p>
              <p>
                By accessing or using Safarnamah, you agree to the terms outlined in this Privacy Policy. If you do not agree with our policies and practices, please do not use our website.
              </p>
            </div>

            <div className="privacy-section">
              <h2>Information We Collect</h2>
              
              <h3>Personal Information</h3>
              <p>We may collect the following types of personal information:</p>
              <ul>
                <li><strong>Contact Information:</strong> Name, email address, phone number when you subscribe to our newsletter, contact us, or engage with our services.</li>
                <li><strong>Account Information:</strong> Username, password, and profile information if you create an account with us.</li>
                <li><strong>Communication Data:</strong> Information you provide when you contact us through email, forms, or chatbot interactions.</li>
                <li><strong>Travel Preferences:</strong> Destination interests, travel dates, and preferences shared through our trip planning tools.</li>
              </ul>

              <h3>Automatically Collected Information</h3>
              <p>When you visit our website, we automatically collect certain information:</p>
              <ul>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers.</li>
                <li><strong>Usage Data:</strong> Pages viewed, time spent on pages, links clicked, and navigation patterns.</li>
                <li><strong>Location Data:</strong> General geographic location based on your IP address.</li>
                <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web beacons, and similar technologies.</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>How We Use Your Information</h2>
              <p>We use the collected information for various purposes:</p>
              <ul>
                <li><strong>Service Delivery:</strong> To provide, maintain, and improve our travel guides, blog content, and trip planning services.</li>
                <li><strong>Communication:</strong> To send newsletters, travel tips, destination updates, and respond to your inquiries.</li>
                <li><strong>Personalization:</strong> To customize your experience and provide relevant destination recommendations.</li>
                <li><strong>Analytics:</strong> To analyze website usage, understand user preferences, and improve our content and services.</li>
                <li><strong>Marketing:</strong> To send promotional materials about new destinations, travel guides, and special features (with your consent).</li>
                <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security concerns.</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights and interests.</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small data files stored on your device that help us recognize you and remember your preferences.
              </p>
              
              <h3>Types of Cookies We Use</h3>
              <ul>
                <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
                <li><strong>Performance Cookies:</strong> Help us understand how visitors interact with our website.</li>
                <li><strong>Functionality Cookies:</strong> Remember your preferences and settings.</li>
                <li><strong>Marketing Cookies:</strong> Track your browsing habits to deliver personalized advertising.</li>
              </ul>
              
              <p>
                You can control cookie settings through your browser preferences. However, disabling certain cookies may limit your ability to use some features of our website.
              </p>
            </div>

            <div className="privacy-section">
              <h2>Information Sharing and Disclosure</h2>
              <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> Third-party vendors who assist us in operating our website, conducting business, or serving our users (e.g., email service providers, analytics platforms).</li>
                <li><strong>Travel Partners:</strong> Hotels, tour operators, and other travel service providers when you request bookings or information.</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of all or a portion of our assets.</li>
                <li><strong>Consent:</strong> With your explicit consent or at your direction.</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul>
                <li>Encryption of data in transit using SSL/TLS protocols</li>
                <li>Secure server infrastructure with regular security updates</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Employee training on data protection and privacy practices</li>
              </ul>
              <p>
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </div>

            <div className="privacy-section">
              <h2>Your Privacy Rights</h2>
              <p>Depending on your location, you may have the following rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request access to the personal information we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations).</li>
                <li><strong>Objection:</strong> Object to the processing of your personal information.</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances.</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service provider.</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for processing where consent is the legal basis.</li>
              </ul>
              <p>
                To exercise these rights, please contact us at privacy@safarnamah.in. We will respond to your request within 30 days.
              </p>
            </div>

            <div className="privacy-section">
              <h2>Third-Party Links and Services</h2>
              <p>
                Our website may contain links to third-party websites, services, or applications that are not operated by us. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
              <p>
                We may integrate third-party services such as:
              </p>
              <ul>
                <li>Social media platforms (Instagram, Facebook, Pinterest)</li>
                <li>Analytics services (Google Analytics)</li>
                <li>Payment processors for bookings</li>
                <li>Email marketing platforms</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>Children's Privacy</h2>
              <p>
                Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will take steps to delete such information.
              </p>
            </div>

            <div className="privacy-section">
              <h2>International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </div>

            <div className="privacy-section">
              <h2>Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
              <p>
                Retention periods vary based on the type of information:
              </p>
              <ul>
                <li>Newsletter subscriptions: Until you unsubscribe</li>
                <li>Contact form submissions: Up to 3 years</li>
                <li>Account information: Until account deletion</li>
                <li>Analytics data: Up to 26 months</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>Newsletter and Marketing Communications</h2>
              <p>
                If you subscribe to our newsletter, you will receive regular updates about travel destinations, tips, and blog posts. You can unsubscribe at any time by:
              </p>
              <ul>
                <li>Clicking the "Unsubscribe" link in any email</li>
                <li>Emailing us at newsletter@safarnamah.in</li>
                <li>Updating your preferences in your account settings</li>
              </ul>
              <p>
                Please note that even if you opt out of marketing communications, we may still send you service-related messages (e.g., account notifications, security alerts).
              </p>
            </div>

            <div className="privacy-section">
              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
              </p>
              <ul>
                <li>Posting the updated policy on this page with a new "Last Updated" date</li>
                <li>Sending an email notification to registered users</li>
                <li>Displaying a prominent notice on our website</li>
              </ul>
              <p>
                We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
              </p>
            </div>

            <div className="privacy-section">
              <h2>Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="contact-details">
                <p><strong>Email:</strong> privacy@safarnamah.in</p>
                <p><strong>General Inquiries:</strong> contact@safarnamah.in</p>
                <p><strong>Website:</strong> <a href="https://www.safarnamah.in">www.safarnamah.in</a></p>
                <p><strong>Response Time:</strong> We aim to respond to all privacy-related inquiries within 48 hours.</p>
              </div>
            </div>

            <div className="privacy-section acknowledgment">
              <h2>Acknowledgment</h2>
              <p>
                By using Safarnamah, you acknowledge that you have read and understood this Privacy Policy and agree to its terms. If you do not agree with this policy, please discontinue use of our website immediately.
              </p>
              <p className="signature">
                — The Safarnamah Team<br />
                Saket and Pallavi
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
